import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

function SystemsGuide() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Connection Systems Guide</h1>
        
        <p className="text-lg text-gray-700 mb-8 leading-relaxed">
          The Connection Tracker is designed to help you understand how well your content, communication, and marketing efforts are actually connecting with real people. Use it to track engagement, audience response, and key touchpoints across different platforms and formats.
        </p>

        <div className="space-y-12">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100">1. Systems You Can Track</h2>
            <p className="text-gray-700 mb-6">You can use this tracker to measure any system or routine related to visibility, communication, and relationship-building. Common examples include:</p>

            <div className="space-y-8">
              {/* Content Based */}
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Social Content Systems</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-blue-800">Feed Posts & Carousels</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> likes, comments, saves, shares, and overall engagement rate.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> These show how strongly your message resonates and whether people are pausing to interact.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800">Stories & Short-Form Video (Reels, Shorts, TikToks)</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> views, taps forward/back, replies, link clicks.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> This shows whether quick-touch content holds attention and inspires interaction.</p>
                  </div>
                </div>
              </div>

              {/* Marketing Based */}
              <div className="bg-green-50 p-6 rounded-lg border border-green-100">
                <h3 className="text-xl font-semibold text-green-900 mb-4">Marketing & Visibility Systems</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-green-800">Email Campaigns & Newsletters</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> open rates, link clicks, replies, unsubscribes.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> This helps you see if your subject lines, offers, and topics are relevant and compelling.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800">Lead Magnet & Landing Pages</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> page visits, button clicks, form submissions.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> These are key points where interest becomes action, showing whether your message and promise are clear.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800">Website CTAs & Navigation Paths</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> where users click from, which pages they land on, and which links they follow.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> This shows if people understand what to do next or if your paths are confusing.</p>
                  </div>
                </div>
              </div>

              {/* Relationship Based */}
              <div className="bg-purple-50 p-6 rounded-lg border border-purple-100">
                <h3 className="text-xl font-semibold text-purple-900 mb-4">Relationship & Communication Systems</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-purple-800">DM & Inbox Conversations</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> how many conversations start, how many continue, and the quality of the responses.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> This shows whether people feel safe, seen, and interested enough to actually talk to you.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-800">Outreach & Collab Requests</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> messages sent, responses received, and follow-up actions.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> This measures how well your outreach language and positioning work when connecting with partners or collaborators.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-800">Client Nurture & Follow-Up</h4>
                    <p className="text-gray-700 text-sm mb-1"><span className="font-medium text-gray-900">Track:</span> check-ins, responses, and opportunities that come from past or current clients.</p>
                    <p className="text-gray-600 text-sm italic"><span className="font-medium text-gray-900 not-italic">Why:</span> This shows how well you're maintaining relationships and staying top-of-mind.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100">2. What to Track in the Connection Tracker</h2>
            <p className="text-gray-700 mb-6">For each content piece or touchpoint, you can log:</p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-gray-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                <h3 className="font-bold text-gray-900 mb-2">Content or Touchpoint Name</h3>
                <p className="text-gray-600 text-sm">What you posted or sent (e.g., "IG Reel: Offer Story," "Email: Monthly Check-In," "DM Outreach to Leads").</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                <h3 className="font-bold text-gray-900 mb-2">Platform or Location</h3>
                <p className="text-gray-600 text-sm">Where it happened (e.g., Instagram feed, stories, email, website, DMs, live video).</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                <h3 className="font-bold text-gray-900 mb-2">Engagement Rate</h3>
                <p className="text-gray-600 text-sm">How strongly people interacted (e.g., likes, comments, shares, saves). This helps you see whether your message and format are landing.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                <h3 className="font-bold text-gray-900 mb-2">Website Clicks</h3>
                <p className="text-gray-600 text-sm">How many people were interested enough to click through to learn more. This tells you whether your call to action is clear and compelling.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                <h3 className="font-bold text-gray-900 mb-2">Replies or DMs</h3>
                <p className="text-gray-600 text-sm">How many people actually talked back or asked a question. This is a powerful signal of trust, curiosity, and connection.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                <h3 className="font-bold text-gray-900 mb-2">Response Quality (1–10)</h3>
                <p className="text-gray-600 text-sm">A simple rating of how aligned and positive the responses felt. High-quality responses show you're attracting the right people with the right message.</p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100">3. Why These Metrics Matter</h2>
            
            <div className="bg-orange-50 p-8 rounded-lg border border-orange-100">
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-800"><strong className="text-orange-900">Engagement</strong> shows whether people are paying attention.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-800"><strong className="text-orange-900">Clicks</strong> show whether people care enough to take the next step.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-800"><strong className="text-orange-900">Replies and DMs</strong> show whether people feel connected and safe enough to talk to you.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-800"><strong className="text-orange-900">Response Quality</strong> shows if you're attracting the right kind of interest, not just any interest.</span>
                </li>
              </ul>

              <h3 className="font-bold text-orange-900 mb-3">Together, these metrics help you:</h3>
              <ul className="space-y-2 text-gray-700 ml-1">
                <li className="flex items-center">
                  <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-orange-600 mr-2" />
                  See which messages, formats, and platforms are worth repeating.
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-orange-600 mr-2" />
                  Identify what needs to be refined, simplified, or retired.
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-orange-600 mr-2" />
                  Make decisions based on real data and real people, not just guesses.
                </li>
              </ul>
            </div>
            
            <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
              <p className="text-lg font-medium text-gray-800">
                Use the Connection Tracker regularly to turn your content and communication into an intentional, trackable system—not just a collection of random posts and messages.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default SystemsGuide;